import Badge from '@atlaskit/badge';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import CopyIcon from '@atlaskit/icon/core/copy';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import DeleteIcon from '@atlaskit/icon/core/delete';
import FilterIcon from '@atlaskit/icon/core/filter';
import SortAscendingIcon from '@atlaskit/icon/core/sort-ascending';
import SortDescendingIcon from '@atlaskit/icon/core/sort-descending';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { type TriggerProps } from '@atlaskit/popup';
import { Box, Flex, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { actionBarState } from '@databases/action-bar/action-bar-state';
import { actionBarPortalState } from '@databases/action-bar/selections-effect';
import {
  getDatabaseContainerType,
  getSelectionsExperimentAttributes,
  SendDatabaseSortEventSources,
  sendSortEvent,
  useAnalytics,
} from '@databases/analytics';
import ContextualMenu, {
  ButtonItem,
  type MenuData,
  type MenuItem,
  useContextualMenu,
} from '@databases/contextual-menu';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt,
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { AskRovoIcon } from '@databases/database-ai-first-creation-ui/icons/ask-rovo-icon';
import { useDialog } from '@databases/dialog';
import { getFieldTypes } from '@databases/fields';
import {
  ActiveHorizontalDragHandlerIcon,
  HideIcon,
  HorizontalDragHandlerIcon,
  InsertColumnAfterIcon,
  InsertColumnBeforeIcon,
  SummationIcon,
} from '@databases/icons';
import { messages } from '@databases/intl/messages';
import { SHORTCUTS, tooltip } from '@databases/keyboard-shortcuts';
import usePanel from '@databases/panel/usePanel';
import usePanelState from '@databases/panel/usePanelState';
import QuickCalculationValues from '@databases/quick-calc/QuickCalculationValues';
import { isQuickCalcDisabledFieldTypes } from '@databases/quick-calc/utils';
import { type HoverProps, useComponent, useKeyboardShortcut } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  doClearEntireFields,
  doDeleteFields,
  doDuplicateEntireFields,
  doInsertFields,
  doMoveFields,
  doSelectNewFields,
} from '@databases/redux/contextual-menu/actions';
import { getService } from '@databases/service-container';
import { IGNORED_TABLE_ELEMENT_CLASS } from '@databases/table-resizer';
import { DatabaseFieldType } from '@databases/types/database';
import {
  type DatabaseFilter,
  type DatabaseFilterUsageType,
  type DatabaseViewSettings,
} from '@databases/types/database/view';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import {
  fieldHasCalculations,
  getDatabaseFieldRequired,
  getDatabaseFields,
} from '@databases/utils/database/utils';
import {
  type ViewChangeType,
  ViewChangeTypes,
} from '@databases/utils/database/view/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { afterNextLayout } from '@databases/utils/lib/raf';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import { useViewUiState } from '@databases/views/useViewUiState';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { ColSelectButtonContainer, DragHandlerWrapper } from './styles';
import { Position } from './type';

const elementBeforeIconWrapper = xcss({
  marginRight: 'space.negative.075',
});

const elementAfterShortCutWrapper = xcss({
  backgroundColor: 'color.background.neutral',
  color: 'color.text.subtlest',
  borderRadius: 'radius.xsmall',
  padding: 'space.050',
  font: 'font.body.small',
  alignSelf: 'flex-end',
});

enum SELECT_FIELD_MENU_ACTIONS {
  ASK_ROVO = 'ask_rovo',
  ADD_FIELD_LEFT = 'add_field_left',
  ADD_FIELD_RIGHT = 'add_field_right',
  DUPLICATE_FIELDS = 'duplicate_fields',
  CLEAR_CELLS = 'clear_cells',
  DELETE_FIELDS = 'delete_fields',
  MOVE_FIELDS_LEFT = 'move_fields_left',
  MOVE_FIELDS_RIGHT = 'move_fields_right',
  FILTER_FIELD = 'filter_field',
  SORT_FIELD_INCREASING = 'sort_field_increasing',
  SORT_FIELD_DECREASING = 'sort_field_decreasing',
  HIDE_FIELDS = 'hide_fields',
}

type SelectFieldMenuProps = {
  contentId: string;
  fieldId: string;
  currentViewId: string;
  viewSettings?: DatabaseViewSettings;
  hoverProps?: HoverProps;
  canEditFields: boolean;
  handleSelectColumn: (e: React.MouseEvent | React.KeyboardEvent) => void;
};

const getIsFieldSelected = (fieldId: string) =>
  getService('SelectionsReader').isFieldSelected(fieldId);

const SelectFieldMenu: FunctionComponent<SelectFieldMenuProps> = ({
  contentId,
  fieldId,
  currentViewId,
  viewSettings,
  hoverProps,
  handleSelectColumn,
  canEditFields,
}) => {
  const selectionsReader = getService('SelectionsReader');
  const isSelectionsPerformanceEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );
  const actionBar = useSnapshot(actionBarState.state);
  const actionBarPortal = useSnapshot(actionBarPortalState);
  const isActionBarEnabled = isSelectionsPerformanceEnabled
    ? actionBarPortal.isVisible
    : actionBar.isActionBarEnabled;

  // This menu only opens for a single selected field, so isSelected is always
  // true and selectedFieldsCount is always 1 when the menu is visible.
  // Guard the heavier reads behind isSelected so unselected columns skip them.
  const isSelected = selectionsReader.isFieldSelected(fieldId);
  const isDeletionDisabled = isSelected && selectionsReader.isDeleteColumnsDisabled();
  const hasFieldBeforeSelection =
    isSelected && selectionsReader.hasFieldBeforeSelection();
  const hasFieldAfterSelection = isSelected && selectionsReader.hasFieldAfterSelection();
  const canInsertFields = canEditFields && isSelected;
  const canDuplicateFields = canEditFields && isSelected;
  const canDeleteFields = canEditFields && isSelected && !isDeletionDisabled;
  const canClearCells = canEditFields && isSelected;
  const canMoveRight = canEditFields && hasFieldAfterSelection;
  const canMoveLeft = canEditFields && hasFieldBeforeSelection;
  const selectionIntent = isSelected ? selectionsReader.getSelectionIntent() : null;
  const isHoverDeleteFieldsButton =
    selectionIntent?.type === 'delete' && selectionIntent?.target === 'fields';

  const { formatMessage } = useIntl();
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const { setViewChange, view } = useView();
  const client = useAnalytics();
  const { hidePanel, showPanel } = usePanel();
  const { getData } = usePanelState();
  const { fieldId: panelFieldId } = getData('FieldSettingsPanel') || {};

  const database = selectDatabaseByContentIdRequired(store.getState(), {
    contentId,
  });
  const field = getDatabaseFieldRequired(database, fieldId);

  const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
    'databases-frontend-visual-refresh-table-m1'
  );
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');

  const menuData: MenuData = {
    type: 'field',
    fieldId,
  };
  const { isMenuOpen, toggleOpenMenu } = useContextualMenu({
    type: 'field',
    fieldId,
  });
  const prevIsSelected = useRef(getIsFieldSelected(fieldId));
  const handleMouseDown = useCallback(
    async (e: React.MouseEvent) => {
      if (!isMenuOpen) {
        await client.sendFieldContextualMenuOpened({
          contentId: contentId,
          databaseContainer: getDatabaseContainerType(),
          ...getSelectionsExperimentAttributes(contentId),
        });
      }
      toggleOpenMenu();
      if (!getIsFieldSelected(fieldId)) {
        handleSelectColumn(e);
        prevIsSelected.current = true;
      } else {
        prevIsSelected.current = false;
      }
    },
    [client, contentId, fieldId, handleSelectColumn, isMenuOpen, toggleOpenMenu]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (getIsFieldSelected(fieldId) && !prevIsSelected.current && !isMenuOpen) {
        // remove selection if the user clicks on the select button again
        handleSelectColumn(e);
      }
    },
    [fieldId, handleSelectColumn, isMenuOpen]
  );

  const handleRightClick = (e: React.MouseEvent) => e.preventDefault();

  const Trigger = useComponent(
    ({ ref, ...triggerProps }: TriggerProps) => (
      <ColSelectButtonContainer
        className={IGNORED_TABLE_ELEMENT_CLASS}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onContextMenu={handleRightClick}
        {...triggerProps}
        {...hoverProps}
        ref={ref}
        data-testid={'field-select-button'}
      >
        <DragHandlerWrapper
          className={TABLE_CLASSNAMES.SELECT_BUTTON}
          isActive={isMenuOpen && getIsFieldSelected(fieldId)}
          isDeletionActive={isHoverDeleteFieldsButton}
          data-interactive
          style={{
            backgroundColor:
              isMenuOpen && getIsFieldSelected(fieldId)
                ? token('color.background.accent.blue.subtler.pressed')
                : isVisualRefreshM1Enabled
                ? token('color.background.accent.gray.subtlest.hovered')
                : null,
          }}
        >
          <div className={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}>
            <ActiveHorizontalDragHandlerIcon
              label={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}
            />
          </div>
          <div className={TABLE_CLASSNAMES.DRAG_HANDLER_ICON}>
            <HorizontalDragHandlerIcon label={TABLE_CLASSNAMES.DRAG_HANDLER_ICON} />
          </div>
        </DragHandlerWrapper>
      </ColSelectButtonContainer>
    ),
    [
      handleMouseDown,
      handleMouseUp,
      hoverProps,
      isMenuOpen,
      fieldId,
      isHoverDeleteFieldsButton,
      isVisualRefreshM1Enabled,
    ]
  );

  const handleInsertFields = useCallback(
    async (position: Position) => {
      const newFieldLabel = formatMessage(
        messages['field-settings.field-name-inline-edit-default-text'],
        {
          defaultFieldName: formatMessage(getFieldTypes()[DatabaseFieldType.TEXT].label),
        }
      );
      await dispatch(
        doSelectNewFields(contentId, () =>
          dispatch(
            doInsertFields(
              contentId,
              currentViewId,
              position === Position.BEFORE,
              newFieldLabel
            )
          )
        )
      );
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [client, contentId, currentViewId, dispatch, formatMessage]
  );

  const handleDuplicateFields = useCallback(() => {
    dispatch(
      doSelectNewFields(contentId, () =>
        dispatch(doDuplicateEntireFields(contentId, view.id))
      )
    );
  }, [dispatch, contentId, view.id]);

  const handleClearFields = useCallback(() => {
    dispatch(doClearEntireFields(contentId));
  }, [contentId, dispatch]);

  const { showDialog } = useDialog();

  const onLeaveDeleteFieldButton = useCallback(() => {
    selectionsActions.clearSelectionIntent();
  }, [selectionsActions]);

  const handleDeleteFields = useCallback(() => {
    const fieldNames: string[] = [];
    const database = selectDatabaseByContentIdRequired(store.getState(), {
      contentId,
    });
    const selectedEntireFieldIds = getService('SelectionsReader').getSelectedFieldIds();
    selectedEntireFieldIds.forEach((fieldId) => {
      if (database.fieldsById[fieldId]) {
        fieldNames.push(database.fieldsById[fieldId].name);
      }
    });
    showDialog('ConfirmDialog', {
      heading: formatMessage(messages['field-settings.delete-field']),
      body: (
        <Stack space="space.150">
          <Box>
            <FormattedMessage
              {...messages[
                'field-settings.delete-multiple-fields-confirmation-message.fields'
              ]}
              values={{
                fieldsNumber: fieldNames.length,
                fields: <Text weight="bold">{fieldNames.join(', ')}</Text>,
              }}
            />
          </Box>
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.additional'
              ]
            )}
          </Box>
        </Stack>
      ),
      yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
      noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
      appearance: 'danger',
      onClose: (result: boolean) => {
        onLeaveDeleteFieldButton();
        if (result) {
          if (getService('SelectionsReader').isFieldInSelection(panelFieldId)) {
            hidePanel();
          }

          if (fieldHasCalculations(fieldId, viewSettings?.calculations)) {
            client.sendRemoveCalculationEvent({
              databaseContainer: getDatabaseContainerType(),
              fieldId,
              fieldType: database.fieldsById[fieldId].type,
              source: 'selectFieldMenu',
              contentId: database.contentId,
            });
          }

          dispatch(doDeleteFields(contentId));

          if (isSelectionsPerformanceEnabled) {
            selectionsActions.deselectAll();
          }

          void client.sendDeleteFieldEvent({
            contentId,
            databaseContainer: getDatabaseContainerType(),
          });
        }
      },
    });
  }, [
    store,
    contentId,
    showDialog,
    formatMessage,
    onLeaveDeleteFieldButton,
    panelFieldId,
    fieldId,
    viewSettings?.calculations,
    dispatch,
    client,
    hidePanel,
    isSelectionsPerformanceEnabled,
    selectionsActions,
  ]);

  const handleMoveFields = useCallback(
    (position: Position) => {
      dispatch(doMoveFields(contentId, viewSettings, position === Position.BEFORE));

      if (isSelectionsPerformanceEnabled) {
        afterNextLayout(() => selectionsActions.selectFields([fieldId]));
      }
    },
    [
      contentId,
      dispatch,
      fieldId,
      isSelectionsPerformanceEnabled,
      selectionsActions,
      viewSettings,
    ]
  );

  const handleSortChange = useCallback(
    (sort: DatabaseViewSettings['sorts'][number]) => {
      const matchingSort = view.settings.sorts.find((s) => s.fieldId === sort.fieldId);
      if (matchingSort) {
        setViewChange({
          type: ViewChangeTypes.VIEW_SORT_UPDATE_VALUE,
          fieldId: sort.fieldId,
          ascending: sort.ascending,
        });
      } else {
        setViewChange({
          type: ViewChangeTypes.VIEW_SORT_APPEND,
          sort,
        });
      }
      void sendSortEvent(
        client,
        sort,
        SendDatabaseSortEventSources.SORT_MENU,
        field ? field.type : undefined,
        contentId
      );
    },
    [client, contentId, field, setViewChange, view.settings.sorts]
  );

  const filters = view.settings.filter as
    | {
        type: DatabaseFilterUsageType;
        value: DatabaseFilter[];
      }
    | undefined;

  const fieldFilters = useMemo(
    () =>
      filters?.value.filter((filter) => 'fieldId' in filter && filter.fieldId === fieldId)
        .length || 0,
    [filters, fieldId]
  );

  const { setActivePanelSetting } = useViewUiState();
  const handleFilterField = useCallback(() => {
    setActivePanelSetting(ViewPanelSettingType.FILTER);
    showPanel('ViewSettingsPanel', {
      canEditViews: canEditFields,
      fieldIdForFilterToCreate: fieldId,
    });
  }, [fieldId, setActivePanelSetting, showPanel, canEditFields]);

  const handleHideFields = useCallback(() => {
    const database = selectDatabaseByContentIdRequired(store.getState(), {
      contentId,
    });
    const fields = getDatabaseFields(database);
    const field = getDatabaseFieldRequired(database, fieldId);
    const viewChange: ViewChangeType = {
      type: ViewChangeTypes.VIEW_FIELDS,
      fieldKey: fieldId,
      fieldValue: false,
      fields,
    };
    setViewChange(viewChange);
    if (isSelectionsPerformanceEnabled) {
      selectionsActions.deselectAll();
    }
    void client.sendHideColumnsEvent({
      source: 'databaseView',
      contentId: contentId,
      databaseContainer: getDatabaseContainerType(),
      hidden: true,
      fieldType: field.type,
    });
  }, [
    client,
    contentId,
    fieldId,
    isSelectionsPerformanceEnabled,
    selectionsActions,
    setViewChange,
    store,
  ]);

  const addFieldLeftMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_LEFT,
      content: formatMessage(messages['field-actions.add-left']),
      closeOnClick: true,
      onClick: () => handleInsertFields(Position.BEFORE),
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.ADD_FIELD_LEFT.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <InsertColumnBeforeIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleInsertFields]
  );

  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const closeSubmenu = useCallback(() => {
    setIsSubmenuOpen(false);
  }, []);
  const toggleSubmenu = useCallback(() => {
    setIsSubmenuOpen((isOpen) => !isOpen);
  }, []);

  const addFieldRightMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_RIGHT,
      content: formatMessage(messages['field-actions.add-right']),
      closeOnClick: true,
      onClick: () => handleInsertFields(Position.AFTER),
      onMouseEnter: closeSubmenu,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.ADD_FIELD_RIGHT.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <InsertColumnAfterIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleInsertFields]
  );

  const duplicateFieldsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.DUPLICATE_FIELDS,
      content: formatMessage(messages['field-actions.duplicate'], {
        fieldCount: 1,
      }),
      closeOnClick: true,
      onClick: handleDuplicateFields,
      onMouseEnter: closeSubmenu,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.DUPLICATE.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <CopyIcon label="" LEGACY_size="medium" spacing="spacious" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleDuplicateFields]
  );

  const clearCellsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.CLEAR_CELLS,
      content: formatMessage(messages['value-actions.clear-cells'], { valuesCount: 2 }),
      closeOnClick: true,
      onClick: handleClearFields,
      onMouseEnter: closeSubmenu,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.CLEAR.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <CrossCircleIcon label="" LEGACY_size="medium" spacing="spacious" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleClearFields]
  );

  const onHoverDeleteFieldButton = useCallback(() => {
    selectionsActions.setSelectionIntent({ type: 'delete', target: 'fields' });
  }, [selectionsActions]);

  const deleteFieldsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.DELETE_FIELDS,
      content: formatMessage(messages['field-actions.delete'], {
        fieldCount: 1,
      }),
      closeOnClick: true,
      onClick: handleDeleteFields,
      onMouseEnter: onHoverDeleteFieldButton,
      onMouseLeave: onLeaveDeleteFieldButton,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.DELETE.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <DeleteIcon
            label=""
            spacing={'spacious'}
            color={
              isHoverDeleteFieldsButton
                ? token('color.icon.danger', '#C9372C')
                : undefined
            }
          />
        </Flex>
      ),
    }),
    [
      formatMessage,
      handleDeleteFields,
      isHoverDeleteFieldsButton,
      onHoverDeleteFieldButton,
      onLeaveDeleteFieldButton,
    ]
  );

  const moveFieldsLeftMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_LEFT,
      content: formatMessage(messages['field-actions.move-left'], {
        fieldCount: 1,
      }),
      closeOnClick: true,
      onClick: () => handleMoveFields(Position.BEFORE),
      onMouseEnter: closeSubmenu,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.MOVE_FIELDS_LEFT.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <ArrowLeftIcon label="" LEGACY_size="medium" spacing="spacious" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleMoveFields]
  );

  const moveFieldsRightMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_RIGHT,
      content: formatMessage(messages['field-actions.move-right'], {
        fieldCount: 1,
      }),
      closeOnClick: true,
      onClick: () => handleMoveFields(Position.AFTER),
      onMouseEnter: closeSubmenu,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SHORTCUTS.MOVE_FIELDS_RIGHT.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <ArrowRightIcon label="" LEGACY_size="medium" spacing="spacious" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleMoveFields]
  );

  const filterFieldItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.FILTER_FIELD,
      content: formatMessage(messages['field-actions.filter']),
      closeOnClick: true,
      onClick: handleFilterField,
      onMouseEnter: closeSubmenu,
      elemAfter:
        fieldFilters > 0 ? (
          <Badge
            style={{
              backgroundColor: token(
                'color.background.accent.blue.subtlest.hovered',
                '#CCE0FF'
              ),
            }}
            testId="filters-badge"
          >
            {fieldFilters}
          </Badge>
        ) : undefined,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <FilterIcon label="" LEGACY_size="medium" spacing="spacious" />
        </Flex>
      ),
    }),
    [formatMessage, handleFilterField, closeSubmenu, fieldFilters]
  );

  const sortFieldIncreasingItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_INCREASING,
      content: formatMessage(messages['field-actions.sort-increasing']),
      closeOnClick: true,
      onClick: () =>
        handleSortChange({
          fieldId,
          ascending: true,
        }),
      onMouseEnter: closeSubmenu,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <SortAscendingIcon label="" spacing="spacious" />
        </Flex>
      ),
    }),
    [closeSubmenu, fieldId, formatMessage, handleSortChange]
  );

  const sortFieldDecreasingItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_DECREASING,
      content: formatMessage(messages['field-actions.sort-decreasing']),
      closeOnClick: true,
      onClick: () =>
        handleSortChange({
          fieldId,
          ascending: false,
        }),
      onMouseEnter: closeSubmenu,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <SortDescendingIcon label="" spacing="spacious" />
        </Flex>
      ),
    }),
    [closeSubmenu, fieldId, formatMessage, handleSortChange]
  );

  const hideFieldsItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.HIDE_FIELDS,
      content: formatMessage(messages['field-actions.hide-fields'], {
        fieldCount: 1,
      }),
      closeOnClick: true,
      onClick: handleHideFields,
      onMouseEnter: closeSubmenu,
      value: { name: SELECT_FIELD_MENU_ACTIONS.HIDE_FIELDS },
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <HideIcon label="" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleHideFields]
  );

  const calculationMenuItemTrigger = useComponent(
    ({ ref, ...triggerProps }) => {
      const isDisabled = isQuickCalcDisabledFieldTypes(field.type);

      return (
        <ToolTip
          content={
            isDisabled
              ? formatMessage(messages['quick.calculations.field-disabled-text'])
              : ''
          }
          position="auto"
        >
          {(tooltipProps) => (
            <Box {...tooltipProps}>
              <ButtonItem
                testId="calculate-menu-item"
                isDisabled={isDisabled}
                iconAfter={
                  isDisabled ? (
                    <StatusInformationIcon spacing="spacious" label={''} />
                  ) : (
                    <ChevronRightIcon label="" LEGACY_size="medium" spacing="spacious" />
                  )
                }
                iconBefore={<SummationIcon label="" />}
                onClick={toggleSubmenu}
                onMouseEnter={() => setIsSubmenuOpen(true)}
                ref={ref}
                {...triggerProps}
              >
                {formatMessage(messages['quick.calculations.placeholder-text'])}
              </ButtonItem>
            </Box>
          )}
        </ToolTip>
      );
    },
    [field, formatMessage, toggleSubmenu]
  );

  const RovoElemBefore = () => {
    useEffect(() => {
      getExperimentValue('databasesAIFC', 'enabled', {
        fireExperimentExposure: true,
      });
    }, []);
    return (
      <Flex xcss={elementBeforeIconWrapper}>
        <AskRovoIcon height={16} width={16} />
      </Flex>
    );
  };

  const askRovoFieldMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ASK_ROVO,
      name: 'ask-rovo',
      closeOnClick: true,
      content: formatMessage(messages['value-actions.ask-rovo']),
      onClick: () => openInlineRovoPrompt(InlineRovoPromptTriggerSource.TableField),
      elemBefore: <RovoElemBefore />,
    }),
    [formatMessage]
  );

  const quickCalculationItem: MenuItem = useMemo(
    () => ({
      key: 'SELECT_VALUE_MENU_ACTIONS.QUICK_CALCULATION',
      name: 'quick-calc',
      closeOnClick: true,
      content: canEditFields ? (
        <QuickCalculationValues
          contentId={contentId}
          field={field}
          Trigger={calculationMenuItemTrigger}
          isOpen={isSubmenuOpen}
          onSelect={() => {
            toggleOpenMenu();
            setIsSubmenuOpen(false);
          }}
          placement="right"
          offset={[0, 2]}
        />
      ) : null,
      value: { name: 'SELECT_VALUE_MENU_ACTIONS.QUICK_CALCULATION' },
    }),
    [
      canEditFields,
      contentId,
      field,
      calculationMenuItemTrigger,
      isSubmenuOpen,
      toggleOpenMenu,
    ]
  );

  const menuItems = useMemo(() => {
    const firstGroup: MenuItem[] = [];
    const secondGroup: MenuItem[] = [];
    const thirdGroup: MenuItem[] = [];
    const fourthGroup: MenuItem[] = [];

    if (
      isDBAiFirstCreationEnabled &&
      getExperimentValue('databasesAIFC', 'enabled', {
        fireExperimentExposure: false,
      })
    ) {
      firstGroup.push(askRovoFieldMenuItem);
    }
    if (canInsertFields) {
      secondGroup.push(addFieldRightMenuItem);
      secondGroup.push(addFieldLeftMenuItem);
    }
    if (canDuplicateFields) {
      secondGroup.push(duplicateFieldsMenuItem);
    }
    if (canClearCells) {
      secondGroup.push(clearCellsMenuItem);
    }
    if (canDeleteFields) {
      secondGroup.push(deleteFieldsMenuItem);
    }
    if (canMoveLeft) {
      thirdGroup.push(moveFieldsLeftMenuItem);
    }
    if (canMoveRight) {
      thirdGroup.push(moveFieldsRightMenuItem);
    }
    fourthGroup.push(quickCalculationItem);
    fourthGroup.push(filterFieldItem);
    fourthGroup.push(sortFieldIncreasingItem);
    fourthGroup.push(sortFieldDecreasingItem);
    fourthGroup.push(hideFieldsItem);
    return [
      { items: firstGroup },
      { items: secondGroup },
      { items: thirdGroup },
      { items: fourthGroup },
    ];
  }, [
    canInsertFields,
    canDuplicateFields,
    canClearCells,
    canDeleteFields,
    canMoveLeft,
    canMoveRight,
    askRovoFieldMenuItem,
    addFieldRightMenuItem,
    addFieldLeftMenuItem,
    duplicateFieldsMenuItem,
    clearCellsMenuItem,
    deleteFieldsMenuItem,
    moveFieldsLeftMenuItem,
    moveFieldsRightMenuItem,
    quickCalculationItem,
    filterFieldItem,
    sortFieldIncreasingItem,
    sortFieldDecreasingItem,
    hideFieldsItem,
    isDBAiFirstCreationEnabled,
  ]);

  useKeyboardShortcut(
    isMenuOpen && canInsertFields ? SHORTCUTS.ADD_FIELD_LEFT.keyboardShortCut : undefined,
    () => handleInsertFields(Position.BEFORE)
  );
  useKeyboardShortcut(
    isMenuOpen && canInsertFields
      ? SHORTCUTS.ADD_FIELD_RIGHT.keyboardShortCut
      : undefined,
    () => handleInsertFields(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && canDuplicateFields ? SHORTCUTS.DUPLICATE.keyboardShortCut : undefined,
    handleDuplicateFields
  );
  useKeyboardShortcut(
    isMenuOpen && canClearCells ? SHORTCUTS.CLEAR.keyboardShortCut : undefined,
    handleClearFields
  );
  useKeyboardShortcut(
    isMenuOpen && canDeleteFields ? SHORTCUTS.DELETE.keyboardShortCut : undefined,
    handleDeleteFields
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveRight ? SHORTCUTS.MOVE_FIELDS_RIGHT.keyboardShortCut : undefined,
    () => handleMoveFields(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveLeft ? SHORTCUTS.MOVE_FIELDS_LEFT.keyboardShortCut : undefined,
    () => () => handleMoveFields(Position.BEFORE)
  );

  return (
    <ContextualMenu
      menuData={menuData}
      items={menuItems}
      trigger={Trigger}
      placement="bottom-start"
      isActionBarEnabled={isActionBarEnabled}
    />
  );
};

export default React.memo(SelectFieldMenu);
